import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import { TooltipWrapper } from "src/shared/components";
import { CheckIcon, ClockIcon, LockIcon, NoSymbolIcon } from "src/static/icons";

import {
  LANE_WIDTH,
  MIN_TILE_HEIGHT,
  PX_PER_HOUR,
  lockKindLabel,
  shiftColor,
  TILE_RAISED_LAYER,
  TShiftScheduleEvent,
} from "../constants";
import { DISABLE_REASONS } from "../types";

interface ShiftProps {
  shift: TShiftScheduleEvent;
  setSelectedShift: (shiftId: number) => void;
}

/** below these heights the tile drops its least important content. xs sits
    above a 45 minute block so 45 and 30 minute shifts share the row layout */
const DENSITY_SM = 74;
const DENSITY_XS = 44;

const densityFor = (height: number) =>
  height < DENSITY_XS ? "xs" : height < DENSITY_SM ? "sm" : "md";

/** Touch devices synthesize hover on tap; only attach lock tooltips where hover is real. */
const useHoverCapable = () => {
  const [capable, setCapable] = useState(
    () => window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setCapable(mq.matches);
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return capable;
};

/** Cause icon for locked tiles. Full shifts stay icon-less (fade + count pill). */
export const LockKindIcon: React.FC<{ kind?: DISABLE_REASONS }> = ({
  kind,
}) => {
  switch (kind) {
    case DISABLE_REASONS.TIME_CONFLICT:
      return <ClockIcon />;
    case DISABLE_REASONS.NO_SPOTS_LEFT:
      return null;
    case DISABLE_REASONS.NOT_MATCHING_PRIMARY_ROLE:
      return <NoSymbolIcon />;
    case DISABLE_REASONS.REGISTRATION_CLOSED:
    default:
      return <LockIcon />;
  }
};

export const Shift: React.FC<ShiftProps> = ({ shift, setSelectedShift }) => {
  const height = Math.max(shift.numberOfBlocks * PX_PER_HOUR, MIN_TILE_HEIGHT);
  const startLabel = shift.start.format("h:mm A");
  const endLabel = shift.end.format("h:mm A");
  const hoverCapable = useHoverCapable();

  // being registered outranks the lock, the same order the old register button used
  const isRegistered = shift.isRegistered?.registered ?? false;
  const isLocked =
    !isRegistered && (shift.isDisableRegistered?.disabled ?? false);
  const lockKind = isLocked ? shift.isDisableRegistered?.kind : undefined;
  const lockReason = isLocked ? shift.isDisableRegistered?.reason ?? "" : "";
  const lockLabel = lockKindLabel(lockKind);
  const showCauseIcon = isLocked && lockKind !== DISABLE_REASONS.NO_SPOTS_LEFT;

  const fillRatio =
    shift.maxVolunteers > 0
      ? shift.registeredVolunteers / shift.maxVolunteers
      : 0;

  const openShift = () => {
    ReactTooltip.hide();
    setSelectedShift(shift.id);
  };

  const tile = (
    <Container
      accent={shiftColor(shift.category)}
      data-density={densityFor(height)}
      data-locked={isLocked}
      role="button"
      tabIndex={0}
      title={`${shift.title} · ${startLabel}–${endLabel} · ${
        shift.registeredVolunteers
      }/${shift.maxVolunteers}${isRegistered ? " · you're registered" : ""}${
        lockReason ? ` · ${lockReason}` : ""
      }`}
      onClick={openShift}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openShift();
        }
      }}
    >
      <TileTitle className="tile-title">{shift.title}</TileTitle>
      <TileTime className="tile-time">
        {startLabel}–{endLabel}
      </TileTime>
      {/* registered → check; locked with a cause icon → icon; full / open →
          count pill (full is fade-only, no person glyph) */}
      {isRegistered ? (
        <StatusMark
          className="tile-badge"
          role="img"
          aria-label="You're signed up"
        >
          <CheckIcon />
        </StatusMark>
      ) : showCauseIcon ? (
        <StatusMark
          className="tile-badge"
          role="img"
          aria-label={lockLabel}
          data-lock-kind={
            lockKind !== undefined ? DISABLE_REASONS[lockKind] : "UNKNOWN"
          }
        >
          <LockKindIcon kind={lockKind} />
        </StatusMark>
      ) : (
        <TilePill className="tile-badge" ratio={fillRatio}>
          {shift.registeredVolunteers}/{shift.maxVolunteers}
        </TilePill>
      )}
    </Container>
  );

  return (
    <Positioner
      style={{
        top: shift.blocksFromTop * PX_PER_HOUR,
        height,
        left: shift.overlapColumn * LANE_WIDTH + 2,
        width: LANE_WIDTH - 4,
        zIndex: shift.overlapColumn + 1,
      }}
    >
      {lockReason && hoverCapable ? (
        <TooltipWrapper
          color="textSecondary"
          icon={tile}
          content={<TooltipText>{lockReason}</TooltipText>}
          direction="top"
          eventDelay={0}
          id={`shift-${shift.id}-lock-tooltip`}
          padding={8}
        />
      ) : (
        tile
      )}
    </Positioner>
  );
};

const Positioner = styled.div`
  position: absolute;
  box-sizing: border-box;

  &:hover,
  &:focus-within {
    z-index: ${TILE_RAISED_LAYER} !important;
  }

  /* TooltipWrapper nests the tile in a span; keep it filling the positioner so
     the tooltip anchors to the tile rather than to a zero-height box */
  > span {
    display: block;
    height: 100%;
  }
`;

const Container = styled.div<{ accent: string }>`
  position: relative;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  background: ${({ accent }) => accent};
  color: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 5px;
  padding: 3px 6px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 1px;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 1px;
  }

  /* shed the least important content as the tile shrinks. the time goes first
     since fulfilment is the thing worth reading at a glance */
  &[data-density="sm"] .tile-time,
  &[data-density="xs"] .tile-time {
    display: none;
  }

  /* too short to stack, so the badge keeps its place on the left and the title
     moves alongside it */
  &[data-density="xs"] {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 0 5px;
    gap: 6px;
  }

  &[data-density="xs"] .tile-badge {
    flex: none;
    order: -1;
    margin-top: 0;
    margin-bottom: 0;
    align-self: center;
  }

  &[data-density="xs"] .tile-title {
    min-width: 0;
  }

  /* unavailable: fade the category colour; cause icon sits in the badge slot */
  &[data-locked="true"] {
    opacity: 0.55;
  }

  &[data-locked="true"]:hover {
    opacity: 0.7;
    filter: none;
  }
`;

/* sits in the pill's slot rather than the corner, so a registered tile reads
   the same shape as an unregistered one */
const StatusMark = styled.span`
  align-self: flex-start;
  margin-top: auto;
  margin-bottom: 4px;
  /* lines the check up with the pill's text rather than its outer edge */
  margin-left: 4px;
  display: flex;
  color: ${({ theme }) => theme.globalConstants.color.white};

  svg {
    width: 17px;
    height: 17px;
  }
`;

const TooltipText = styled.p`
  font-size: 12px;
  color: white;
  font-weight: 700;
`;

const TileTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TileTime = styled.span`
  font-size: 12px;
  line-height: 1.3;
  opacity: 0.88;
  white-space: nowrap;
  overflow: hidden;
`;

/* fulfilment at a glance: empty reads danger, partly filled warning, full
   primary, and over capacity gray */
const TilePill = styled.span<{ ratio: number }>`
  align-self: flex-start;
  margin-top: auto;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 20px;
  padding: 0 6px;
  line-height: 1.6;
  white-space: nowrap;
  background: ${({ theme, ratio }) =>
    ratio > 1
      ? theme.globalConstants.color.backgroundGrayDark
      : ratio === 1
      ? theme.globalConstants.color.backgroundPrimaryLight
      : ratio === 0
      ? theme.globalConstants.color.backgroundDangerLight
      : theme.globalConstants.color.backgroundWarningLight};
  color: ${({ theme, ratio }) =>
    ratio > 1
      ? theme.globalConstants.color.textSecondary
      : ratio === 1
      ? theme.globalConstants.color.textPrimary
      : ratio === 0
      ? theme.globalConstants.color.textDanger
      : theme.globalConstants.color.textWarning};
`;
